import { type RefObject, useEffect } from 'react';
import { isNil } from 'lodash/fp';
import { LEFT_CLICK, RIGHT_CLICK } from './useOutsideClickHandler';

const testForPreviousSibling = (
    currentElement: HTMLElement,
    targetElement: HTMLElement,
    containerElement: HTMLElement
) => {
    if (!(containerElement.contains(targetElement) || containerElement.contains(currentElement))) {
        return false;
    }
    let sibling = currentElement.previousSibling;
    while (sibling) {
        if (sibling.contains(targetElement)) {
            return true;
        }
        sibling = sibling.previousSibling;
    }
    return false;
};

interface UseOutsideModalClickHandlerOptions {
    containerRef: RefObject<HTMLElement | null>;
    elementRef?: RefObject<HTMLElement | null>;
    handler: (event: Event) => void;
    enabled?: boolean;
    clickThroughPreviousSibling?: boolean;
}

export function useOutsideModalClickHandler({
    containerRef,
    elementRef,
    handler,
    enabled = true,
    clickThroughPreviousSibling = true
}: UseOutsideModalClickHandlerOptions) {
    useEffect(() => {
        if (
            !enabled ||
            isNil(containerRef?.current) ||
            (!clickThroughPreviousSibling && isNil(elementRef?.current))
        ) {
            return;
        }

        function listener(event: Event) {
            const targetElement = event.target as HTMLElement;
            const ownElement = elementRef?.current;
            const containerElement = containerRef.current;

            const isSibling =
                clickThroughPreviousSibling &&
                ownElement instanceof HTMLElement &&
                containerElement instanceof HTMLElement &&
                testForPreviousSibling(ownElement, targetElement, containerElement);

            // Children of Modal (content of Modal) are updated before this listener is triggered,
            // in the case of a child being removed from the DOM, the reference of the element will still exist
            // and the following check will fail because the element doesn't exist, so the Modal cannot contain it.
            // so we need to check if the element is contained in the document at all, if not, do not count it as outside click
            // this situation should only occur for elements that are children of the Modal
            const isOutsideContainer =
                !containerRef.current?.contains(targetElement) &&
                document.body.contains(targetElement);

            const isClickThrough = targetElement.hasAttribute('data-click-through');

            if (!(isSibling || isOutsideContainer || isClickThrough)) {
                return;
            }

            handler(event);
        }

        document.addEventListener(LEFT_CLICK, listener, true);
        document.addEventListener(RIGHT_CLICK, listener, true);

        return () => {
            document.removeEventListener(LEFT_CLICK, listener, true);
            document.removeEventListener(RIGHT_CLICK, listener, true);
        };
    }, [containerRef, elementRef, handler, enabled, clickThroughPreviousSibling]);
}
